import { cn, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { minMaxAxisDomain } from '@/components/Charts/utils';
import {
  Explainer,
  ExplainerBody,
  ExplainerContent,
  ExplainerCopy,
  ExplainerTitle,
  ExplainerTrigger,
} from '@/components/NCExplainers/Explainer';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { PlotIndicatorCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotIndicatorCard';
import { isNullish } from '@/utils/booleans';
import { getHexFromString } from '@/utils/getHexFromString';

type ChartDataPoint = Record<string, number | string> & {
  yearLabel: string;
};

type FloraFaunaDiversityProps = {
  data: PlotReportFactElement[];
  kingdomLabel?: string;
  labelTarget?: string;
  categoryShades?: Record<string, string>;
  unit?: string;
};

export const FloraFaunaDiversity = (props: FloraFaunaDiversityProps) => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.floraFauna.diversity.labels.heading')}>
      <FloraFaunaDiversityCard {...props} />
    </PlotCardWrapper>
  );
};

const FloraFaunaDiversityCard = ({
  data,
  kingdomLabel = 'kingdom',
  categoryShades = { flora: '#5dbd26', fauna: '#c0880e' },
  unit,
}: FloraFaunaDiversityProps) => {
  const { t } = useTranslation();

  const groupsData = getGroupsData(data, kingdomLabel);

  if (isNullish(data)) {
    return null;
  }
  const cardProps = {
    'data-testid': 'index-card-nca',
    explainer: <FloraFaunaDiversityExplainer />,
  };

  const TITLE = t(`shared.ncaDetail.details.floraFauna.diversity.labels.heading`);

  const headerProps = {
    title: TITLE,
    className: 'lg:grid-cols-[1fr_1fr]',
  };
  const footerProps = {
    logos: ['tnfd', 'gri', 'sbtn', 'cdp', 'csrd', 'sdg', 'npi', 'eudr', 'sai'],
  };

  const statProps = {
    children: <Stats groupsData={groupsData} unit={unit} />,
  };

  const graphHeaderProps = {
    heading: TITLE,
    popover: {
      title: t('shared.ncaDetail.details.floraFauna.diversity.explainer.floraFaunaDiversity.title'),
      body: t('shared.ncaDetail.details.floraFauna.diversity.explainer.floraFaunaDiversity.body'),
    },
    className: 'w-full px-6 sm:px-0',
  };

  const graphProps = {
    children: !groupsData?.length ? null : (
      <GraphTile groupsData={groupsData} categoryShades={categoryShades} unit={unit} />
    ),
    graphHeaderProps,
  };

  return (
    <PlotIndicatorCard
      cardProps={cardProps}
      headerProps={headerProps}
      footerProps={footerProps}
      statProps={statProps}
      graphProps={graphProps}
    />
  );
};

const Stats = ({ groupsData, unit }: { groupsData: ChartDataPoint[]; unit?: string }) => {
  const { t } = useTranslation();
  const groupsCount = groupsData.reduce<{ [key: string]: number }>((acc, cur) => {
    Object.entries(cur).forEach(([group, value]) => {
      if (group !== 'yearLabel') {
        acc[`${group}`] = (acc[`${group}`] ?? 0) + (value as number);
      }
    });
    return acc;
  }, {});

  return (
    <Stack className='gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
      <ul className='list-inside list-disc space-y-1 ml-6 w-full'>
        {Object.entries(groupsCount).map(([category, value]) => (
          <li key={category}>
            <span className='typography-body1Semibold'>{category.charAt(0).toUpperCase() + category.slice(1)}</span>:{' '}
            {value} {unit ? t(`global.ui.unit.${unit as UnitEnum}`) : ''}
          </li>
        ))}
      </ul>
    </Stack>
  );
};

const getGroupsData = (data: PlotReportFactElement[], kingdomLabel: string): ChartDataPoint[] => {
  const sortedDates: string[] = [
    ...new Set(data.filter((entry) => !isNullish(entry.date)).map((entry) => entry.date as string)),
  ].sort();

  return sortedDates.map((date) => {
    const dateData = data.filter((entry) => entry.date === date);

    const categoriesData = dateData.reduce(
      (acc, cur) => {
        const kingdom = cur.labels?.find((label) => label.name === kingdomLabel)?.value as string | undefined;
        if (!kingdom) return acc;

        acc[`${kingdom}`] = (acc[`${kingdom}`] || 0) + (cur.value as number);
        return acc;
      },
      {} as { [key: string]: number },
    );

    const year = new Date(date).getFullYear().toString();

    return {
      yearLabel: year,
      ...categoriesData,
    };
  });
};

const GraphTile = ({
  groupsData,
  categoryShades,
  unit,
}: {
  groupsData: ChartDataPoint[];
  categoryShades?: Record<string, string>;
  unit?: string;
}) => {
  const { t } = useTranslation();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const hasSelectedCategories = selectedCategories.length > 0;

  const getTitle = (category: string) => category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ');

  const categories = Object.keys(groupsData?.[0] ?? {}).filter((key) => key !== 'yearLabel');

  const BAR_CATEGORIES = Object.values(categories).map((category) => ({
    dataKey: category,
    key: category,
    id: category,
    fill: categoryShades?.[`${category}`] ?? getHexFromString(category),
    color: categoryShades?.[`${category}`] ?? getHexFromString(category),
    barColors: [categoryShades?.[`${category}`] ?? getHexFromString(category)],
    label: getTitle(category),
    shape: ChartShape.bar,
    stackId: category,
    barSize: 20,
    unit,
  }));

  const filteredBars = hasSelectedCategories
    ? BAR_CATEGORIES.filter((item) => selectedCategories.includes(item.dataKey))
    : BAR_CATEGORIES;

  const handleLegendClick = (category: string) => {
    let newSelectedCategories = selectedCategories.includes(category)
      ? [...selectedCategories.filter((c) => c !== category)]
      : [...selectedCategories, category];

    if (newSelectedCategories.length === categories.length) {
      newSelectedCategories = [];
    }

    setSelectedCategories(newSelectedCategories);
  };

  const legendItems: ChartLegendBarItem[] = [
    {
      label: t('global.misc.all'),
      color: '',
      shape: 'line',
      onClick: () => setSelectedCategories([]),
      className: cn({
        'bg-transparent rounded-3xl border border-text-secondary px-2 py-1': true,
        'bg-neutral-hover': selectedCategories.length === 0,
      }),
    },
    ...categories.map((category) => ({
      label: getTitle(category),
      color: categoryShades?.[`${category}`] ?? getHexFromString(category),
      onClick: () => handleLegendClick(category),
      shape: 'column',
      className: cn({
        'bg-transparent rounded-3xl border border-text-secondary px-2 py-1': true,
        'bg-neutral-hover': selectedCategories.includes(category),
      }),
    })),
  ];

  const widgetConfiguration = {
    name: 'flora_fauna_diversity_graph',
    type: WidgetType.o_composite_graph,
    visible: true,
    relatedData: {
      'data-testid': 'flora-fauna-diversity-graph-chart',
      data: groupsData,
      chartConfig: filteredBars,
      legendItems,
      xAxisProps: {
        dataKey: 'yearLabel',
        type: 'category',
      },
      yAxisProps: {
        domain: minMaxAxisDomain(0.98, 1.05),
        label: t('shared.ncaDetail.details.floraFauna.diversity.labels.yAxis'),
        labelProps: {
          position: {
            x: 45,
            y: 10,
          },
        },
      },
    },
  };

  return <WidgetSlotRenderer nestedWidget={widgetConfiguration} />;
};

const FloraFaunaDiversityExplainer = () => {
  const { t } = useTranslation();

  return (
    <Explainer>
      <ExplainerTrigger data-testid='flora-fauna-diversity-explainer'>
        {t('global.howDoWeMeasureThis')}
      </ExplainerTrigger>
      <ExplainerContent>
        <ExplainerTitle>
          {t(`shared.ncaDetail.details.floraFauna.diversity.explainer.howDoWeMeasureThis.title`)}
        </ExplainerTitle>
        <Stack className='items-start pb-6'>
          <ExplainerBody>
            <ExplainerCopy
              body={t(`shared.ncaDetail.details.floraFauna.diversity.explainer.howDoWeMeasureThis.body`)}
            />
          </ExplainerBody>
          {/* TODO: MVP-9692 update methodology link to correct page when available */}
          {/* <MethodologyLink to={''} /> */}
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};
